import { Component, computed, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { SonaUsageFacade } from '@sona/angular-auth-api';
import { SonaIconComponent } from '@sona/angular-ui-components';
import { useI18n } from '@sona/angular-util-common';
import { SubscriptionPlan, SubscriptionPlanSchema } from '@sona/shared-model-common';
import { BrnTooltipImports } from '@spartan-ng/brain/tooltip';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmTooltipComponent, HlmTooltipTriggerDirective } from '@spartan-ng/ui-tooltip-helm';
import { toast } from 'ngx-sonner';

import { SubscriptionService } from '../services/subscription.service';

@Component({
    selector: 'sona-upgrade-button',
    standalone: true,
    imports: [ HlmButtonDirective, RouterLink, TranslatePipe, SonaIconComponent, HlmTooltipComponent,
               BrnTooltipImports, HlmTooltipTriggerDirective ],
    template: `
        @switch (_currentStatus()) {
            @case ('trialing') {
                <hlm-tooltip>
                    <button hlmBtn (click)="endTrial()" hlmTooltipTrigger [disabled]="_endTrialButtonDisabled() || _usageFacade.isLoading()">
                        {{ 'PAYMENTS.COMPONENT.UPGRADE_BUTTON.END_TRIAL.LABEL' | translate }}
                    </button>
                    <div class="tw-max-w-60" *brnTooltipContent>
                        {{ 'PAYMENTS.COMPONENT.UPGRADE_BUTTON.END_TRIAL.TOOLTIP' | translate }}
                    </div>
                </hlm-tooltip>
            }
            @default {
                @if (_visible()) {
                    <button hlmBtn routerLink="/upgrade">
                        <sona-icon name="sparkle" size="small" appearance="bold" class="tw-mr-2" />
                        @if (plan(); as plan) {
                            {{ 'PAYMENTS.COMPONENT.UPGRADE_BUTTON.UPGRADE_TO' | translate:{ plan: plan } }}
                        } @else {
                            {{ 'PAYMENTS.COMPONENT.UPGRADE_BUTTON.UPGRADE' | translate }}
                        }
                    </button>
                }
            }
        }
    `,
    host: {
        class: 'tw-contents'
    }
})
export class UpgradeButtonComponent {

    private readonly _subscriptionService = inject(SubscriptionService);
    protected readonly _usageFacade = inject(SonaUsageFacade);
    private readonly _i18n = useI18n();
    protected readonly _currentPlan = computed(() => this._usageFacade.usage()?.subscription.plan);
    protected readonly _currentStatus = computed(() => this._usageFacade.usage()?.subscription.status);
    protected readonly _endTrialButtonDisabled = signal(false);

    /**
     * The plan to upgrade to.
     */
    readonly plan = input<SubscriptionPlan>();

    /**
     * Whether the upgrade button should be visible:
     * 1. if the user is on an unpaid plan, always show the upgrade button
     * 2. if the user is on a trial for a paid plan, show the end trial button
     * 2. if the user is on an active paid plan, show the upgrade button only if a target plan is set
     */
    protected readonly _visible = computed(() => {
        const options = SubscriptionPlanSchema.options;
        const currentPlan = this._currentPlan() ?? 'free';
        const targetPlan = this.plan();

        if (!targetPlan) {
            return [ 'free', 'pilot' ].includes(currentPlan);
        }

        return options.indexOf(currentPlan) < options.indexOf(targetPlan);
    });

    async endTrial() {
        this._endTrialButtonDisabled.set(true);

        try {
            await this._subscriptionService.endTrial();
            toast.success(this._i18n().t('PAYMENTS.COMPONENT.UPGRADE_BUTTON.END_TRIAL.FEEDBACK.SUCCESS'));

            this._usageFacade.updateUsage();

            setTimeout(async () => {
                this._usageFacade.updateUsage();
                this._endTrialButtonDisabled.set(false);
            }, 10_000);
        } catch (e) {
            this._usageFacade.updateUsage();
            toast.error(this._i18n().t('PAYMENTS.COMPONENT.UPGRADE_BUTTON.END_TRIAL.FEEDBACK.ERROR'));
            this._endTrialButtonDisabled.set(false);
        }

    }
}
